/**
 * captureScreenshot — render the live r3f scene into an offscreen
 * WebGLRenderTarget at any resolution, in any of three camera modes:
 *
 *   • 'current'  — clone the live perspective camera at its current pose
 *                  (image aspect adjusted to the requested width/height).
 *   • 'top-down' — orthographic camera placed above the scene's AABB
 *                  centre, looking straight down -Z, with up = world +Y.
 *                  Frustum sized to fit the AABB plus padding. Mirrors
 *                  the 2D viewer convention (path along image X,
 *                  encoder-side along image Y).
 *   • 'custom'   — caller supplies a CameraState (positionMm, targetMm).
 *                  Lets you reproduce a saved shot exactly.
 *
 * Why an offscreen target instead of `gl.domElement.toBlob()`:
 *   - Live canvas is created with `preserveDrawingBuffer: false` for
 *     perf — `toBlob` would race with the next frame's clear.
 *   - The screenshot resolution decouples from the live canvas DPR;
 *     export 4K PNGs even from a 1× display.
 *   - Switching the camera doesn't require disturbing OrbitControls.
 *
 * Result:
 *   { blob, widthPx, heightPx, camera, boundingBoxMm, mode }
 *
 * Caller composes the URL via URL.createObjectURL() or downloads
 * directly with an <a download="…"> trick. The blob mime is
 * `image/png` by default; jpeg / webp also supported with `quality`.
 *
 * Caveats:
 *   - Assumes the scene has finished loading (GLBs in cache). When
 *     called during a Suspense fallback, only what's currently in the
 *     scene graph gets rendered.
 *   - The renderer's pixel ratio, clear colour, and current render
 *     target are saved + restored, so live rendering is not disturbed.
 */

import {
  Box3,
  Color,
  type ColorRepresentation,
  type Light,
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderTarget,
  WebGLRenderer,
} from 'three';
import type { CameraState, Vec3 } from '../../types.js';
import { computeTopDownOrtho } from './viewerHelpers.js';

export type ScreenshotMode = 'current' | 'top-down' | 'custom';
export type ScreenshotFormat = 'png' | 'jpeg' | 'webp';

export interface ScreenshotOptions {
  /** Camera mode. Default: `'current'`. */
  mode?: ScreenshotMode;
  /** Required when `mode === 'custom'`. Ignored otherwise. */
  camera?: CameraState;
  /**
   * Image dimensions in pixels.
   * - Both supplied: the rendered frustum (top-down) is adjusted to
   *   match the resulting aspect; the perspective camera (current /
   *   custom) gets its `aspect` set to `width / height`.
   * - One supplied: the other is derived to keep the scene's aspect
   *   ratio (top-down) or the live canvas aspect (current / custom).
   * - Neither: canvas CSS dimensions × `pixelRatio`.
   */
  width?: number;
  height?: number;
  format?: ScreenshotFormat;
  /** 0..1 — JPEG / WebP quality. Default 0.92. Ignored for PNG. */
  quality?: number;
  /**
   * Padding factor around the scene AABB in `top-down` mode. Default
   * 1.1 (10 % margin on each side). Higher = more whitespace.
   */
  paddingFactor?: number;
  /**
   * Background colour:
   *   - `null` (default) → transparent PNG, matches the live canvas
   *     contract. JPEG falls back to white because the format has no
   *     alpha channel.
   *   - `'#RRGGBB'` / any three.js ColorRepresentation → solid fill.
   */
  backgroundColor?: ColorRepresentation | null;
  /**
   * Pixel ratio for offscreen renders. Default = renderer's current
   * pixel ratio (matches the live canvas sharpness on high-DPI
   * displays). Set explicitly for fixed-resolution exports.
   */
  pixelRatio?: number;
  /**
   * Multiplier applied to every Light in the scene during the offscreen
   * render. Default 1.35 — screenshots tend to feel dim compared to
   * the live canvas because the live tone-mapping pipeline picks up
   * the surrounding monitor luminance, which the offscreen render
   * doesn't. Pass 1 to disable, > 1 for a brighter result. Restored
   * after capture.
   */
  exposureBoost?: number;
}

export interface ScreenshotResult {
  blob: Blob;
  widthPx: number;
  heightPx: number;
  /** Effective camera state used for the render. Useful to log + reproduce. */
  camera: CameraState;
  /** Scene AABB at capture time. Useful to overlay annotations later. */
  boundingBoxMm: { min: Vec3; max: Vec3 };
  mode: ScreenshotMode;
}

const DEFAULT_QUALITY = 0.92;
const DEFAULT_PADDING_FACTOR = 1.1;

interface CaptureContext {
  gl: WebGLRenderer;
  scene: Scene;
  liveCamera: PerspectiveCamera | OrthographicCamera | null;
  /**
   * Live OrbitControls target — reported back in the result for
   * `'current'` mode so the caller can save + replay. Optional; omitting
   * it just means the result's `targetMm` is `[0, 0, 0]` for current
   * mode.
   */
  liveTargetMm?: Vec3;
  bbox: Box3;
}

export async function captureScreenshot(
  ctx: CaptureContext,
  opts: ScreenshotOptions = {},
): Promise<ScreenshotResult> {
  const { gl, scene, liveCamera, bbox } = ctx;
  const mode: ScreenshotMode = opts.mode ?? 'current';
  const format: ScreenshotFormat = opts.format ?? 'png';
  const quality = opts.quality ?? DEFAULT_QUALITY;
  const padding = opts.paddingFactor ?? DEFAULT_PADDING_FACTOR;
  const dpr = opts.pixelRatio ?? gl.getPixelRatio();

  const canvas = gl.domElement;
  const cssW = Math.max(1, canvas.clientWidth || canvas.width / dpr);
  const cssH = Math.max(1, canvas.clientHeight || canvas.height / dpr);

  let width = opts.width;
  let height = opts.height;

  let renderCamera: PerspectiveCamera | OrthographicCamera;
  let resultCameraState: CameraState;

  if (mode === 'top-down') {
    if (bbox.isEmpty()) {
      throw new Error("captureScreenshot('top-down'): scene bounding box is empty");
    }
    const size = bbox.getSize(new Vector3());
    const sceneAspect = Math.max(1, size.x) / Math.max(1, size.y);

    if (width !== undefined && height === undefined) {
      height = Math.max(1, Math.round(width / sceneAspect));
    } else if (height !== undefined && width === undefined) {
      width = Math.max(1, Math.round(height * sceneAspect));
    } else if (width === undefined && height === undefined) {
      width = Math.max(1, Math.round(cssW * dpr));
      height = Math.max(1, Math.round(width / sceneAspect));
    }
    const imageAspect = width! / height!;

    // Shared framing math — keeps the screenshot pixel-consistent with the
    // live `projection='orthographic'` top-down view.
    const f = computeTopDownOrtho(bbox, imageAspect, padding);
    const ortho = new OrthographicCamera(
      f.left,
      f.right,
      f.top,
      f.bottom,
      f.near,
      f.far,
    );
    ortho.up.set(...f.up);
    ortho.position.set(...f.position);
    ortho.lookAt(new Vector3(...f.target));
    ortho.updateMatrixWorld();
    renderCamera = ortho;
    resultCameraState = {
      positionMm: [...f.position],
      targetMm: [...f.target],
      upAxis: 'Z',
    };
  } else if (mode === 'custom') {
    const cs = opts.camera;
    if (!cs) {
      throw new Error("captureScreenshot('custom'): `opts.camera` is required");
    }
    if (!liveCamera) {
      throw new Error('captureScreenshot: no active camera to clone from');
    }
    width ??= Math.max(1, Math.round(cssW * dpr));
    height ??= Math.max(1, Math.round(cssH * dpr));
    const persp = liveCamera.clone() as PerspectiveCamera;
    persp.position.set(...cs.positionMm);
    persp.up.set(0, 0, 1);
    persp.lookAt(new Vector3(...cs.targetMm));
    persp.aspect = width / height;
    persp.updateProjectionMatrix();
    renderCamera = persp;
    resultCameraState = {
      positionMm: [...cs.positionMm],
      targetMm: [...cs.targetMm],
      upAxis: cs.upAxis,
    };
  } else {
    if (!liveCamera) {
      throw new Error('captureScreenshot: no active camera');
    }
    width ??= Math.max(1, Math.round(cssW * dpr));
    height ??= Math.max(1, Math.round(cssH * dpr));
    const persp = liveCamera.clone() as PerspectiveCamera;
    persp.aspect = width / height;
    persp.updateProjectionMatrix();
    renderCamera = persp;
    resultCameraState = {
      positionMm: [persp.position.x, persp.position.y, persp.position.z],
      targetMm: ctx.liveTargetMm ?? [0, 0, 0],
      upAxis: 'Z',
    };
  }

  // Allocate target. depthBuffer required for proper occlusion; stencil
  // not used by any of our materials, so skip it for memory.
  //
  // colorSpace = sRGB: critical for screenshots. The live canvas runs
  // through an sRGB framebuffer, so what the user sees is gamma-encoded.
  // A render target without explicit colorSpace stores linear pixels —
  // when we readback into a canvas2D ImageData (which is sRGB), the
  // image looks much darker than the live view (mid-greys land at ~22 %
  // instead of 50 %). Marking the target sRGB tells the renderer to
  // encode on write, so readRenderTargetPixels yields the right bytes.
  const target = new WebGLRenderTarget(width!, height!, {
    depthBuffer: true,
    stencilBuffer: false,
    colorSpace: SRGBColorSpace,
  });

  // Boost lights for the offscreen render so the result reads as bright
  // as the live canvas (which benefits from the user's surrounding UI
  // luminance). Tracked + restored in the finally block.
  const exposureBoost = opts.exposureBoost ?? 1.35;
  const boostedLights: Array<{ light: Light; orig: number }> = [];
  if (exposureBoost !== 1) {
    scene.traverse((obj) => {
      const maybeLight = obj as Partial<Light> & { intensity?: number };
      if (
        typeof maybeLight.intensity === 'number' &&
        (obj as Light).isLight
      ) {
        boostedLights.push({
          light: obj as Light,
          orig: maybeLight.intensity,
        });
        (obj as Light).intensity = maybeLight.intensity * exposureBoost;
      }
    });
  }

  // Save renderer state so live rendering doesn't get disturbed.
  const prevTarget = gl.getRenderTarget();
  const prevAlpha = gl.getClearAlpha();
  const prevColor = new Color();
  gl.getClearColor(prevColor);
  const prevBg = scene.background;

  try {
    if (opts.backgroundColor === null || opts.backgroundColor === undefined) {
      gl.setClearColor(0x000000, 0);
      scene.background = null;
    } else {
      const bg = new Color(opts.backgroundColor);
      gl.setClearColor(bg, 1);
      scene.background = bg;
    }
    gl.setRenderTarget(target);
    gl.clear(true, true, false);
    gl.render(scene, renderCamera);

    const buffer = new Uint8Array(width! * height! * 4);
    gl.readRenderTargetPixels(target, 0, 0, width!, height!, buffer);

    // WebGL's framebuffer is bottom-left origin; canvas2D is top-left.
    // Flip rows in place into a copy with the right Y orientation.
    const flipped = new Uint8ClampedArray(width! * height! * 4);
    const rowBytes = width! * 4;
    for (let y = 0; y < height!; y++) {
      const srcOff = (height! - 1 - y) * rowBytes;
      const dstOff = y * rowBytes;
      flipped.set(buffer.subarray(srcOff, srcOff + rowBytes), dstOff);
    }

    const imageData = new ImageData(flipped, width!, height!);
    const blob = await encodeImageData(imageData, format, quality);

    return {
      blob,
      widthPx: width!,
      heightPx: height!,
      camera: resultCameraState,
      boundingBoxMm: {
        min: [bbox.min.x, bbox.min.y, bbox.min.z],
        max: [bbox.max.x, bbox.max.y, bbox.max.z],
      },
      mode,
    };
  } finally {
    // Restore mutated state in reverse order of mutation.
    for (const { light, orig } of boostedLights) light.intensity = orig;
    target.dispose();
    gl.setRenderTarget(prevTarget);
    gl.setClearColor(prevColor, prevAlpha);
    scene.background = prevBg;
  }
}

function mimeFor(format: ScreenshotFormat): string {
  return format === 'png'
    ? 'image/png'
    : format === 'jpeg'
      ? 'image/jpeg'
      : 'image/webp';
}

async function encodeImageData(
  imageData: ImageData,
  format: ScreenshotFormat,
  quality: number,
): Promise<Blob> {
  const w = imageData.width;
  const h = imageData.height;
  const mime = mimeFor(format);

  // Prefer OffscreenCanvas — works in workers + avoids DOM mutation
  // during a screenshot taken on a render-loop tick.
  if (typeof OffscreenCanvas !== 'undefined') {
    const oc = new OffscreenCanvas(w, h);
    const cx = oc.getContext('2d');
    if (!cx) throw new Error('Failed to acquire OffscreenCanvas 2D context');
    cx.putImageData(imageData, 0, 0);
    return oc.convertToBlob(format === 'png' ? { type: mime } : { type: mime, quality });
  }

  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const cx = c.getContext('2d');
  if (!cx) throw new Error('Failed to acquire HTMLCanvasElement 2D context');
  cx.putImageData(imageData, 0, 0);
  return new Promise<Blob>((resolve, reject) => {
    c.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      mime,
      format === 'png' ? undefined : quality,
    );
  });
}
